import { useSyncExternalStore } from 'react';

/**
 * เกณฑ์ที่ผู้ใช้ตั้งเอง และรอบการตรวจตามเกณฑ์นั้น
 *
 * ────────────────────────────────────────────────────────────────
 *  หลักที่ไฟล์นี้ทั้งไฟล์อยู่ใต้มัน
 * ────────────────────────────────────────────────────────────────
 *
 * ระบบไม่ได้ตัดสินว่าใครเหมาะ — ระบบตอบว่า **ใครตรงกับเกณฑ์ที่ผู้ใช้ตั้งไว้**
 * เกณฑ์เป็นของผู้ใช้ ดุลพินิจจึงยังเป็นของผู้ใช้ ระบบแค่ตรวจตามที่สั่ง
 *
 * ผลที่ตามมาสองข้อ ซึ่งห้ามเปลี่ยนเพื่อความสวยงามของหน้าจอ
 *
 *   1 ห้ามสรุปผลเป็น "คะแนน" หรือ "N จาก M"
 *     ตัวเลขจะถูกอ่านเป็นคะแนนความเหมาะสมทันที แล้วหน้าจอจะกลายเป็นการจัดอันดับ
 *     ซึ่งเป็นอำนาจของคณะกรรมการตามกฎหมาย ไม่ใช่ของระบบ
 *     ให้เขียนเป็นคำว่าเข้าเกณฑ์ข้อไหน ไม่เข้าข้อไหน
 *
 *   2 "ไม่มีข้อมูล" (unknown) ไม่เท่ากับ "ไม่เข้าเกณฑ์" (fail)
 *     unknown แปลว่าระบบไม่มีข้อมูลจะตอบ ไม่ได้แปลว่าท่านนั้นไม่เคยทำ
 *     ถ้าทำให้สองอย่างนี้หน้าตาเหมือนกัน เท่ากับตัดสินคนด้วยช่องว่างในฐานข้อมูล
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ ทำไมต้องเก็บทุกรอบ
 * ────────────────────────────────────────────────────────────────
 *
 * การวนปรับเกณฑ์ไปเรื่อย ๆ จนคนที่อยากได้โผล่ขึ้นมา คือสิ่งที่ทำให้คำสั่งแต่งตั้ง
 * ถูกโต้แย้งได้ ถ้าเก็บทุกรอบไว้ เกณฑ์ที่เปลี่ยนกลางทางจะเห็น ซึ่งเป็นข้อดี
 * ถ้าไม่เก็บ หน้าจอนี้จะกลายเป็นเครื่องมือหาเหตุผลย้อนหลังให้คนที่เลือกไว้แล้ว
 */

/* ================================================================
   ชนิดข้อมูล
   ================================================================ */

export type CriterionKind = 'computed' | 'assistant';

/**
 * เกณฑ์ที่ตรวจได้จากข้อมูลที่มีจริง
 *
 * ⚠ มีได้เท่านี้ ห้ามเพิ่มกฎที่ข้อมูลตอบไม่ได้
 *   ทุกกฎในนี้ต้องชี้ไปที่ฟิลด์ที่มีอยู่จริงใน RotationCandidate / RotationMatch
 *   ถ้าอยากได้กฎใหม่ ต้องมีข้อมูลมาก่อน ไม่ใช่เขียนกฎแล้วหวังว่าจะมีข้อมูล
 */
export type ComputedSpec =
  | { rule: 'tenureOver'; years: number }
  | { rule: 'unitCanSpare' }
  | { rule: 'notRetiringWithin'; years: number }
  | { rule: 'notCandidateElsewhere' };

export interface Criterion {
  /** ป้ายสั้นที่ใช้อ้างในตาราง เช่น ค1 */
  id: string;
  label: string;
  kind: CriterionKind;
  /** null เมื่อเป็นเกณฑ์ที่ต้องให้ผู้ช่วยอ่าน */
  spec: ComputedSpec | null;
  addedAt: string;
}

export type Verdict = 'pass' | 'fail' | 'unknown';

export interface CellResult {
  verdict: Verdict;
  /** เหตุผลของคำตอบ — ต้องมีเสมอ ผู้ใช้ต้องตรวจย้อนได้ว่าทำไมถึงตอบแบบนี้ */
  detail: string;
}

/** ข้อเท็จจริงรายคนที่หน้าจอประกอบมาให้ — ทั้งหมดมาจากข้อมูลจริง ไม่มีค่าที่เดาเอง */
export interface CandidateFacts {
  key: string;
  name: string;
  monthsInPosition: number | null;
  fromUnitRatio: number;
  toUnitRatio: number;
  retireInYears: number | null;
  /** จำนวนอัตราว่างอื่นในรอบเดียวกันที่ท่านนี้อยู่ในข่าย */
  otherVacancies: number;
}

export interface Round {
  round: number;
  at: string;
  criteria: Criterion[];
  /** key ของคน → id ของเกณฑ์ → ผล */
  results: Record<string, Record<string, CellResult>>;
}

export interface BoardState {
  criteria: Criterion[];
  rounds: Round[];
}

/* ================================================================
   ตัวตรวจ — ไม่เรียกผู้ช่วย ไม่ใช้เครือข่าย ตอบทันที
   ================================================================ */

function durationYears(months: number): string {
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y === 0) return `${m} เดือน`;
  return m === 0 ? `${y} ปี` : `${y} ปี ${m} เดือน`;
}

/**
 * ตรวจเกณฑ์หนึ่งข้อกับคนหนึ่งคน
 *
 * ⚠ ทุกทางออกต้องมี detail เสมอ ห้ามคืนคำตอบเปล่า
 *   ผู้ใช้ต้องกดดูได้ว่าทำไมระบบถึงตอบแบบนั้น ไม่งั้นก็เท่ากับเชื่อระบบลอย ๆ
 */
export function evaluate(spec: ComputedSpec, f: CandidateFacts): CellResult {
  switch (spec.rule) {
    case 'tenureOver': {
      if (f.monthsInPosition === null) {
        return { verdict: 'unknown', detail: 'ไม่มีวันครองตำแหน่งในข้อมูล' };
      }
      const need = spec.years * 12;
      const txt = durationYears(f.monthsInPosition);
      return f.monthsInPosition >= need
        ? { verdict: 'pass', detail: `ครองมาแล้ว ${txt}` }
        : { verdict: 'fail', detail: `ครองมาแล้ว ${txt} ยังไม่ถึงเกณฑ์` };
    }

    case 'unitCanSpare': {
      return f.fromUnitRatio >= f.toUnitRatio
        ? { verdict: 'pass', detail: 'หน่วยเดิมมีคนครบกว่าหน่วยปลายทาง' }
        : { verdict: 'fail', detail: 'หน่วยเดิมมีคนน้อยกว่าหน่วยปลายทาง' };
    }

    /* ⚠ ในชุดข้อมูลสาธิต birthDate เป็นค่าที่สร้างขึ้น (enhancedMeta.generatedFields)
       วันเกษียณจึงยังไม่ใช่ของจริง — หน้าจอต้องขึ้นคำเตือนคู่กับเกณฑ์ข้อนี้เสมอ
       ดู SYNTHETIC_RULES ข้างล่าง */
    case 'notRetiringWithin': {
      if (f.retireInYears === null) {
        return { verdict: 'unknown', detail: 'ไม่มีวันเกิดในข้อมูล จึงคำนวณวันเกษียณไม่ได้' };
      }
      return f.retireInYears > spec.years
        ? { verdict: 'pass', detail: `เหลืออีก ${f.retireInYears} ปีถึงเกษียณ` }
        : { verdict: 'fail', detail: `เหลืออีก ${f.retireInYears} ปีถึงเกษียณ` };
    }

    case 'notCandidateElsewhere': {
      return f.otherVacancies === 0
        ? { verdict: 'pass', detail: 'ไม่ได้อยู่ในข่ายของอัตราอื่นในรอบนี้' }
        : { verdict: 'fail', detail: `อยู่ในข่ายของอีก ${f.otherVacancies} อัตราในรอบนี้` };
    }
  }
}

/** กฎที่ข้อมูลต้นทางยังเป็นค่าที่สร้างขึ้น — ต้องขึ้นคำเตือนคู่กันเสมอ */
export const SYNTHETIC_RULES: ReadonlyArray<ComputedSpec['rule']> = ['notRetiringWithin'];

/**
 * เกณฑ์สำเร็จรูปที่ตรวจได้ทันที
 *
 * ⚠ รายการนี้คือ "ทั้งหมด" ที่ข้อมูลตอบได้ตอนนี้ — ไม่ใช่ตัวอย่าง
 *   อย่าเพิ่มรายการที่ยังไม่มีข้อมูลรองรับเพื่อให้หน้าจอดูมีตัวเลือกเยอะ
 */
export const PRESETS: ReadonlyArray<{ label: string; spec: ComputedSpec }> = [
  { label: 'ครองตำแหน่งปัจจุบันเกิน 4 ปี', spec: { rule: 'tenureOver', years: 4 } },
  { label: 'ครองตำแหน่งปัจจุบันเกิน 2 ปี', spec: { rule: 'tenureOver', years: 2 } },
  { label: 'หน่วยเดิมมีคนครบกว่าหน่วยปลายทาง', spec: { rule: 'unitCanSpare' } },
  { label: 'ไม่เกษียณภายใน 2 ปี', spec: { rule: 'notRetiringWithin', years: 2 } },
  { label: 'ไม่ได้อยู่ในข่ายของอัตราอื่นในรอบนี้', spec: { rule: 'notCandidateElsewhere' } },
];

/**
 * คำที่บอกว่าเกณฑ์นั้นตอบไม่ได้เลยด้วยข้อมูลชุดนี้
 *
 * ⚠ ไม่ใช่การเดาความหมายของประโยค เป็นแค่การจับคำเพื่อ **เตือนก่อนกด**
 *   ถ้าจับไม่ได้ ผู้ใช้ก็ยังเพิ่มเกณฑ์ได้ตามปกติ แล้วจะได้คำตอบว่าไม่มีข้อมูล
 *   พังไปทางที่ปลอดภัย คือไม่ห้ามใครทำอะไร แค่บอกล่วงหน้าเท่าที่รู้
 *
 * ที่มาของรายการนี้คือ enhancedMeta.generatedFields ในไฟล์ข้อมูล ซึ่งระบุเองว่า
 * education / performance / competencies / training / languages / awards /
 * decorations / achievements / salary / disciplinary / personalProfile เป็นค่าที่สร้างขึ้น
 */
const NO_DATA_WORDS = [
  'วุฒิ', 'การศึกษา', 'ปริญญา', 'จบ',
  'ผลการประเมิน', 'ประเมิน', 'สมรรถนะ',
  'วินัย', 'ลงโทษ', 'ร้องเรียน',
  'เงินเดือน', 'ค่าตอบแทน',
  'อบรม', 'หลักสูตร', 'ภาษา', 'รางวัล', 'เครื่องราช',
];

export function looksUnanswerable(text: string): string | null {
  const hit = NO_DATA_WORDS.find((w) => text.includes(w));
  return hit ?? null;
}

/* ================================================================
   ที่เก็บ — ต่ออัตรา
   ================================================================ */

const KEY = 'yana.rotation.criteria.v1';

type Store = Record<string, BoardState>;

let store: Store = load();
const listeners = new Set<() => void>();

function load(): Store {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Store) : {};
  } catch {
    return {};
  }
}

function save(next: Store): void {
  store = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* เขียนไม่ได้ก็ใช้ต่อในหน้านี้ได้ แค่ไม่ค้างข้ามการรีเฟรช */
  }
  for (const fn of listeners) fn();
}

const EMPTY: BoardState = { criteria: [], rounds: [] };

function snapshot(): Store {
  return store;
}

export function useCriteria(posNo: string | null): BoardState {
  const all = useSyncExternalStore(subscribe, snapshot, () => ({}) as Store);
  if (!posNo) return EMPTY;
  return all[posNo] ?? EMPTY;
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** อ่านกระดานของอัตราหนึ่งนอกรอบ React — ตัวสร้างเอกสารเสนอเรียกใช้
 *  ⚠ อ่านอย่างเดียว ห้ามแก้ผ่านทางนี้ ทุกการเขียนต้องผ่าน save() เพื่อให้หน้าจออัปเดต */
export function boardOf(posNo: string): BoardState {
  return store[posNo] ?? EMPTY;
}

function nextLabel(existing: Criterion[]): string {
  return `ค${existing.length + 1}`;
}

export function addCriterion(
  posNo: string,
  input: { label: string; kind: CriterionKind; spec: ComputedSpec | null },
): void {
  const b = boardOf(posNo);
  const c: Criterion = {
    id: nextLabel(b.criteria),
    label: input.label,
    kind: input.kind,
    spec: input.spec,
    addedAt: new Date().toISOString(),
  };
  save({ ...store, [posNo]: { ...b, criteria: [...b.criteria, c] } });
}

export function removeCriterion(posNo: string, id: string): void {
  const b = boardOf(posNo);
  save({ ...store, [posNo]: { ...b, criteria: b.criteria.filter((c) => c.id !== id) } });
}

/**
 * บันทึกผลหนึ่งรอบ
 *
 * ⚠ เก็บ criteria ที่ใช้ในรอบนั้นไว้ในตัวรอบเอง ไม่ใช่อ้างไปที่รายการปัจจุบัน
 *   เพราะรายการปัจจุบันเปลี่ยนได้ ถ้าอ้างไป ประวัติจะเล่าเรื่องผิดย้อนหลัง
 */
export function recordRound(
  posNo: string,
  criteria: Criterion[],
  results: Record<string, Record<string, CellResult>>,
): void {
  const b = boardOf(posNo);
  const round: Round = {
    round: b.rounds.length + 1,
    at: new Date().toISOString(),
    criteria: criteria.map((c) => ({ ...c })),
    results,
  };
  save({ ...store, [posNo]: { ...b, rounds: [...b.rounds, round] } });
}

export function clearBoard(posNo: string): void {
  const next = { ...store };
  delete next[posNo];
  save(next);
}

/* ================================================================
   สรุปผลรายคน — เป็นคำ ไม่ใช่คะแนน
   ================================================================ */

/**
 * ⚠ ห้ามเปลี่ยนเป็นตัวเลข ดูเหตุผลที่หัวไฟล์
 *   "เข้าครบ" · "ไม่เข้า ค2" · "ค3 ตอบไม่ได้" คือรูปแบบที่ถูก
 *   "3 จาก 4" คือรูปแบบที่ผิด เพราะอ่านเป็นคะแนนทันที
 */
export function summariseRow(
  criteria: Criterion[],
  row: Record<string, CellResult> | undefined,
): string {
  if (criteria.length === 0) return 'ยังไม่ได้ตั้งเกณฑ์';
  const fails: string[] = [];
  const unknowns: string[] = [];
  for (const c of criteria) {
    const v = row?.[c.id]?.verdict;
    if (v === 'fail') fails.push(c.id);
    else if (v === 'unknown' || v === undefined) unknowns.push(c.id);
  }
  const parts: string[] = [];
  if (fails.length > 0) parts.push(`ไม่เข้า ${fails.join(' ')}`);
  if (unknowns.length > 0) parts.push(`${unknowns.join(' ')} ตอบไม่ได้`);
  return parts.length === 0 ? 'เข้าครบ' : parts.join(' · ');
}
